import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Megaphone, Pencil, Trash2, X } from "lucide-react";
import { api, fileUrl } from "../../api/client";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import { formatTaipeiDateTime } from "../../lib/datetime";

// 公佈欄(業主 2026-09-25):
//   > 在打卡頁面還要增加公佈欄 布告欄內容每個人都可以新增/移除 而且也可以增加圖片
//
// **在系統裡編,門口打卡機只輪播**(業主選的)。每則是文字 + 最多一張圖。
// 每個人都能移除任何一則 —— 所以移除是丟進回收桶,七天內救得回來,
// 而這件事要寫在畫面上:不寫的話,沒有人敢按,或者按了才發現是別人貼的。

export interface BulletinPost {
  id: number;
  body: string;
  image_url: string | null;
  author: string;
  created_at: string;
  /** 改過才有值(業主 2026-10-05:貼的人與老闆能改)。 */
  edited_at?: string | null;
  edited_by?: string | null;
  /** 這個登入者能不能改這一則 —— 後端算,畫面只照著顯示。 */
  can_edit?: boolean;
}

type PostImage = { file_id: number; url: string } | null;

/** 「已編輯 · MM/DD HH:mm」(台北時間;共用 formatTaipeiDateTime 的 `YYYY/MM/DD HH:mm` 去掉年)。 */
function editedLabel(iso: string): string {
  const text = formatTaipeiDateTime(iso);
  return `已編輯 · ${text.length >= 16 ? text.slice(5) : text}`;
}

/** 就地修改:文字 + 圖片(保留 / 換 / 拿掉),儲存走 PATCH,失敗跳錯誤提示、編輯框留著。 */
function PostEditor({ post, onDone }: { post: BulletinPost; onDone: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState(post.body);
  // keep:沿用原本那張(只有網址,沒有 file_id);null:拿掉;物件:剛上傳的新圖
  const [image, setImage] = useState<PostImage | "keep">(post.image_url ? "keep" : null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (imageFileId: number | null) =>
      api.patch(`/bulletin/${post.id}`, { body: body.trim(), image_file_id: imageFileId }),
    onSuccess: () => {
      toast.push("改好了", "success");
      void queryClient.invalidateQueries({ queryKey: ["bulletin"] });
      onDone();
    },
    onError: (err: unknown) => toast.push(err instanceof Error ? err.message : "改不了", "error"),
  });

  const pickImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      setImage(await api.upload<{ file_id: number; url: string }>("/bulletin/images", form));
    } catch (err) {
      setError(err instanceof Error ? err.message : "圖片傳不上去");
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    if (body.trim() === "" && image === null) {
      setError("公告要有文字或圖片");
      return;
    }
    setError(null);
    // 後端的 PATCH 是「整個換掉」:保留原圖就要把原本的 file_id 帶回去。
    const keptId = Number(post.image_url?.split("/").pop());
    save.mutate(image === "keep" ? keptId : image ? image.file_id : null);
  };

  const shownUrl = image === "keep" ? post.image_url : image ? image.url : null;

  return (
    <div className="flex flex-col gap-2">
      <textarea
        aria-label="修改公告內容"
        rows={3}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        className="w-full rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] p-2 text-sm text-[var(--text)]"
      />
      {shownUrl ? (
        <div className="flex items-start gap-2">
          <img src={fileUrl(shownUrl)} alt="要附上的圖片" className="max-h-40 rounded-[var(--radius)]" />
          <Button
            size="sm"
            variant="ghost"
            aria-label="拿掉這張圖"
            icon={<X aria-hidden="true" size={14} />}
            onClick={() => setImage(null)}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--radius)] border border-[var(--border-strong)] px-2.5 py-1.5 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]">
          <ImagePlus aria-hidden="true" size={16} />
          {uploading ? "上傳中…" : shownUrl ? "換一張圖片" : "加一張圖片"}
          <input
            type="file"
            accept="image/*"
            aria-label={shownUrl ? "換一張圖片" : "加一張圖片"}
            className="sr-only"
            disabled={uploading}
            onChange={(event) => void pickImage(event)}
          />
        </label>
        <Button variant="primary" size="sm" loading={save.isPending} disabled={uploading} onClick={submit}>
          儲存
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          取消
        </Button>
      </div>
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function BulletinPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");
  const [image, setImage] = useState<{ file_id: number; url: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const posts = useQuery({
    queryKey: ["bulletin"],
    queryFn: () => api.get<BulletinPost[]>("/bulletin"),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["bulletin"] });

  const create = useMutation({
    mutationFn: () => api.post("/bulletin", { body: body.trim(), image_file_id: image?.file_id ?? null }),
    onSuccess: () => {
      setBody("");
      setImage(null);
      toast.push("貼上去了,門口的打卡機下一輪就看得到", "success");
      refresh();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "貼不上去"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/bulletin/${id}`),
    onSuccess: () => {
      toast.push("已丟進回收桶,七天內救得回來", "success");
      refresh();
    },
    onError: (err: unknown) => toast.push(err instanceof Error ? err.message : "移除不了", "error"),
  });

  const pickImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      setImage(await api.upload<{ file_id: number; url: string }>("/bulletin/images", form));
    } catch (err) {
      setError(err instanceof Error ? err.message : "圖片傳不上去");
    } finally {
      setUploading(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (body.trim() === "" && image === null) {
      setError("公告要有文字或圖片");
      return;
    }
    setError(null);
    create.mutate();
  };

  const list = Array.isArray(posts.data) ? posts.data : [];

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <h2 className="m-0 flex items-center gap-2 text-xl font-semibold text-[var(--text)]">
        <Megaphone aria-hidden="true" size={20} />
        公佈欄
      </h2>
      <p className="m-0 text-sm text-[var(--text-2)]">
        這裡貼的公告會在門口的打卡機上輪播。每個人都可以貼、也可以移除任何一則;
        移除的會進回收桶,七天內救得回來。貼的人與老闆可以修改,改過的會標「已編輯」。
      </p>

      <form
        onSubmit={submit}
        className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3"
      >
        <label htmlFor="bulletin-body" className="text-sm font-medium text-[var(--text-2)]">
          公告內容
        </label>
        <textarea
          id="bulletin-body"
          rows={3}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          className="w-full rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] p-2 text-sm text-[var(--text)]"
        />
        {image ? (
          <div className="flex items-start gap-2">
            <img src={fileUrl(image.url)} alt="要附上的圖片" className="max-h-40 rounded-[var(--radius)]" />
            <Button
              size="sm"
              variant="ghost"
              aria-label="拿掉這張圖"
              icon={<X aria-hidden="true" size={14} />}
              onClick={() => setImage(null)}
            />
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--radius)] border border-[var(--border-strong)] px-2.5 py-1.5 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]">
            <ImagePlus aria-hidden="true" size={16} />
            {uploading ? "上傳中…" : "附一張圖片"}
            <input
              type="file"
              accept="image/*"
              aria-label="附一張圖片"
              className="sr-only"
              disabled={uploading}
              onChange={(event) => void pickImage(event)}
            />
          </label>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={uploading}>
            貼上公佈欄
          </Button>
        </div>
        {error ? (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
      </form>

      {posts.isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {!posts.isLoading && list.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">目前沒有公告。</p>
      ) : null}
      <ul aria-label="公告" className="m-0 flex list-none flex-col gap-3 p-0">
        {list.map((post) => (
          <li
            key={post.id}
            className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3"
          >
            {editingId === post.id ? (
              <PostEditor post={post} onDone={() => setEditingId(null)} />
            ) : (
              <>
                {post.body ? <p className="m-0 whitespace-pre-wrap text-[var(--text)]">{post.body}</p> : null}
                {post.image_url ? (
                  <img src={fileUrl(post.image_url)} alt="公告圖片" className="max-h-80 self-start rounded-[var(--radius)]" />
                ) : null}
              </>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--text-3)]">
              <span>
                {post.author || "(已不在職)"}・{formatTaipeiDateTime(post.created_at)}
                {post.edited_at ? `・${editedLabel(post.edited_at)}` : ""}
              </span>
              <span className="inline-flex items-center gap-1">
              {post.can_edit && editingId !== post.id ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Pencil aria-hidden="true" size={14} />}
                  aria-label={`修改這則公告(${post.body.slice(0, 12) || "圖片公告"})`}
                  onClick={() => setEditingId(post.id)}
                >
                  修改
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                icon={<Trash2 aria-hidden="true" size={14} />}
                aria-label={`移除這則公告(${post.body.slice(0, 12) || "圖片公告"})`}
                onClick={() => {
                  if (window.confirm("移除這則公告嗎?它會進回收桶,七天內救得回來。")) {
                    remove.mutate(post.id);
                  }
                }}
              >
                移除
              </Button>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
